import React, { useEffect, useMemo, useState } from "react";
import Select from "./ui/Select";
import axiosInstance from "../utils/axiosInstance";

const toNumber = (v) => {
  const cleaned = String(v ?? "").replace(/[^\d.-]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatRialPlain = (n) =>
  new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 0 }).format(
    Math.round(toNumber(n)),
  );

const formatRial = (n) => `${formatRialPlain(n)} ریال`;

/**
 * نرخ ارز گمرکی (Static) — فقط یک مقدار دارد
 * مقادیر نمونه هستند؛ نرخ‌های واقعی را جایگزین کنید.
 */
const CUSTOMS_STATIC_RATES = [
  {
    id: "C_USD",
    slug: "USD",
    name: "دلار امریکا",
    rate: "1310661.00",
    date: "ثابت",
  },
  { id: "C_EUR", slug: "EUR", name: "یورو", rate: "1551964.00", date: "ثابت" },
  {
    id: "C_CNY",
    slug: "CNY",
    name: "یوان چین",
    rate: "188931.00",
    date: "ثابت",
  },
  {
    id: "C_GBP",
    slug: "GBP",
    name: "پوند انگلیس",
    rate: "1787007.00",
    date: "ثابت",
  },
  {
    id: "C_AED",
    slug: "AED",
    name: "درهم امارات",
    rate: "356885.00",
    date: "ثابت",
  },
];

function TariffCalculator() {
  // Inputs
  const [arzesheKol, setArzesheKol] = useState("");
  const [tarefe, setTarefe] = useState("");

  // Rate source: api (مرکز مبادله) | customs (گمرکی)
  const [rateSource, setRateSource] = useState("api");

  // API fetch state
  const [apiRates, setApiRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Currency selection
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  // For API only
  const [rateSide, setRateSide] = useState("sell"); // sell | buy

  // Manual override
  const [customRate, setCustomRate] = useState(false);
  const [manualRate, setManualRate] = useState("");

  // Insurance manual override
  const [customInsurance, setCustomInsurance] = useState(false);
  const [manualInsurance, setManualInsurance] = useState("");

  // Fetch rates only when source is API
  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (rateSource !== "api") {
        setLoading(false);
        setError("");
        setApiRates([]);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const res = await axiosInstance.get(
          "/customs/currency-exchange-rates/",
        );
        if (!alive) return;
        setApiRates(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!alive) return;
        setError("خطا در دریافت نرخ ارز مرکز مبادله");
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [rateSource]);

  // Normalize active list to a consistent shape for selection + labels
  const activeRates = useMemo(() => {
    if (rateSource === "api") {
      return apiRates.map((x) => ({
        id: x.id,
        slug: x.slug,
        name: x.name,
        date: x.date,
        sell_price: x.sell_price,
        buy_price: x.buy_price,
      }));
    }

    return CUSTOMS_STATIC_RATES.map((x) => ({
      id: x.id,
      slug: x.slug,
      name: x.name,
      date: x.date ?? "ثابت",
      rate: x.rate,
    }));
  }, [rateSource, apiRates]);

  // Keep selection when switching source (match by slug)
  useEffect(() => {
    if (!activeRates.length) {
      setSelectedCurrency(null);
      return;
    }

    if (!selectedCurrency) {
      setSelectedCurrency(activeRates[0]);
      return;
    }

    const match = activeRates.find((x) => x.slug === selectedCurrency.slug);
    setSelectedCurrency(match ?? activeRates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateSource, activeRates.length]);

  // Dropdown options include prices in label
  const currencyOptions = useMemo(() => {
    return activeRates.map((item) => {
      const base = `${item.slug} — ${item.name}`;
      const pricePart =
        rateSource === "api"
          ? ` | فروش: ${formatRialPlain(
              item.sell_price,
            )} | خرید: ${formatRialPlain(item.buy_price)}`
          : ` | نرخ: ${formatRialPlain(item.rate)}`;

      return {
        value: item.id,
        data: item,
        label: base + pricePart,
      };
    });
  }, [activeRates, rateSource]);

  const selectedOption = useMemo(() => {
    if (!selectedCurrency) return null;

    const base = `${selectedCurrency.slug} — ${selectedCurrency.name}`;
    const pricePart =
      rateSource === "api"
        ? ` | فروش: ${formatRialPlain(
            selectedCurrency.sell_price,
          )} | خرید: ${formatRialPlain(selectedCurrency.buy_price)}`
        : ` | نرخ: ${formatRialPlain(selectedCurrency.rate)}`;

    return {
      value: selectedCurrency.id,
      data: selectedCurrency,
      label: base + pricePart,
    };
  }, [selectedCurrency, rateSource]);

  // Final exchange rate used in calculations
  const exchangeRate = useMemo(() => {
    if (customRate) return toNumber(manualRate);
    if (!selectedCurrency) return 0;

    if (rateSource === "customs") {
      return toNumber(selectedCurrency.rate);
    }

    // API
    return rateSide === "buy"
      ? toNumber(selectedCurrency.buy_price)
      : toNumber(selectedCurrency.sell_price);
  }, [customRate, manualRate, rateSource, rateSide, selectedCurrency]);

  // Calculations
  const totalValue = toNumber(arzesheKol);
  const tariffPercent = toNumber(tarefe);

  const calc = useMemo(() => {
    const arzesheRiali = totalValue * exchangeRate;
    const bime = customInsurance
      ? toNumber(manualInsurance)
      : arzesheRiali / 2000;
    const cif = arzesheRiali + bime;
    const gomraki = (cif * tariffPercent) / 100;
    const mabnayeMaliat = cif + gomraki;
    const dahDarsad = mabnayeMaliat / 10;
    const helalAhmar = gomraki / 100;
    const pasmand = cif / 2000;
    const varizi = gomraki + dahDarsad + helalAhmar + pasmand;
    const maliat2Darsad = mabnayeMaliat / 50;
    const jameKol = varizi + maliat2Darsad;
    const STD = arzesheRiali * 0.0008;
    const VOC = arzesheRiali * 0.0007;
    const davazdahHezarom = arzesheRiali * 0.012;
    return {
      arzesheRiali,
      bime,
      cif,
      gomraki,
      mabnayeMaliat,
      dahDarsad,
      helalAhmar,
      pasmand,
      varizi,
      maliat2Darsad,
      jameKol,
      STD,
      VOC,
      davazdahHezarom,
    };
  }, [
    totalValue,
    exchangeRate,
    tariffPercent,
    customInsurance,
    manualInsurance,
  ]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: 14,
      minHeight: 44,
      boxShadow: "none",
      borderColor: "rgba(148,163,184,.45)",
    }),
    menu: (base) => ({ ...base, borderRadius: 14, overflow: "hidden" }),
    option: (base) => ({ ...base, padding: 12 }),
  };

  return (
    <div className="w-full flex justify-center px-4 py-10 mt-16">
      <div className="w-full max-w-5xl">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="p-6 sm:p-8 border-b border-slate-200">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">
              محاسبه هزینه‌های گمرکی
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              نرخ ارز را از «مرکز مبادله» (API) یا «نرخ ارز گمرکی» (ثابت) انتخاب
              کنید.
            </p>
          </div>

          <div className="p-6 sm:p-8 space-y-4">
            {/* Inputs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Total */}
              <div className="rounded-2xl border border-slate-200 p-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  ارزش کل (ارز خارجی)
                </label>
                <input
                  type="number"
                  value={arzesheKol}
                  onChange={(e) => setArzesheKol(e.target.value)}
                  placeholder="مثلاً 10000"
                  min={0}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>

              {/* Currency card with SMALL switch inside */}
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <label className="text-sm font-medium text-slate-700">
                    نرخ ارز
                  </label>

                  <div className="flex items-center gap-2">
                    {/* Mini source switch */}
                    <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
                      <button
                        type="button"
                        onClick={() => setRateSource("api")}
                        className={`px-2 py-1 rounded-lg text-xs transition ${
                          rateSource === "api"
                            ? "bg-slate-900 text-white"
                            : "bg-transparent text-slate-700 hover:bg-white"
                        }`}
                        title="نرخ ارز مرکز مبادله"
                      >
                        مرکز مبادله
                      </button>
                      <button
                        type="button"
                        onClick={() => setRateSource("customs")}
                        className={`px-2 py-1 rounded-lg text-xs transition ${
                          rateSource === "customs"
                            ? "bg-slate-900 text-white"
                            : "bg-transparent text-slate-700 hover:bg-white"
                        }`}
                        title="نرخ ارز گمرکی"
                      >
                        گمرکی
                      </button>
                    </div>

                    {/* buy/sell only meaningful for API */}
                    <select
                      value={rateSide}
                      onChange={(e) => setRateSide(e.target.value)}
                      className="rounded-lg border border-slate-200 px-2 py-1 text-xs outline-none bg-white"
                      disabled={customRate || rateSource !== "api"}
                      title="نوع نرخ"
                    >
                      <option value="sell">فروش</option>
                      <option value="buy">خرید</option>
                    </select>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                  <input
                    type="checkbox"
                    checked={customRate}
                    onChange={() => setCustomRate((v) => !v)}
                  />
                  وارد کردن نرخ ارز دستی
                </label>

                {customRate ? (
                  <input
                    type="number"
                    value={manualRate}
                    onChange={(e) => setManualRate(e.target.value)}
                    placeholder="مثلاً 740348"
                    min={0}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
                  />
                ) : rateSource === "api" && loading ? (
                  <div className="text-sm text-slate-500">در حال دریافت...</div>
                ) : rateSource === "api" && error ? (
                  <div className="text-sm text-rose-600">{error}</div>
                ) : (
                  <>
                    <Select
                      styles={selectStyles}
                      options={currencyOptions}
                      value={selectedOption}
                      onChange={(opt) => setSelectedCurrency(opt?.data ?? null)}
                      placeholder="ارز را انتخاب کنید"
                      isClearable
                    />

                    {selectedCurrency ? (
                      <div className="mt-2 text-xs text-slate-500">
                        منبع:{" "}
                        {rateSource === "api"
                          ? "نرخ ارز مرکز مبادله"
                          : "نرخ ارز گمرکی"}
                        {" — "}
                        تاریخ: {selectedCurrency.date}
                        {" — "}
                        نرخ اعمال‌شده: {formatRial(exchangeRate)}
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              {/* Tariff */}
              <div className="rounded-2xl border border-slate-200 p-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  مأخذ تعرفه (درصد)
                </label>
                <input
                  type="number"
                  value={tarefe}
                  onChange={(e) => setTarefe(e.target.value)}
                  placeholder="مثلاً 5"
                  min={0}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>
            </div>

            {/* Insurance */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-700">بیمه</div>
                  <div className="text-xs text-slate-500 mt-1">
                    حالت پیش‌فرض: ارزش ریالی / 2000
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={customInsurance}
                    onChange={() => setCustomInsurance((v) => !v)}
                  />
                  وارد کردن بیمه دستی
                </label>
              </div>

              {customInsurance ? (
                <div className="mt-3">
                  <input
                    type="number"
                    value={manualInsurance}
                    onChange={(e) => setManualInsurance(e.target.value)}
                    placeholder="مبلغ بیمه (ریال)"
                    min={0}
                    className="w-full sm:max-w-sm rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </div>
              ) : null}
            </div>

            {/* Results */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <ResultCard
                title="ارزش ریالی"
                value={formatRial(calc.arzesheRiali)}
              />
              <ResultCard title="بیمه" value={formatRial(calc.bime)} />
              <ResultCard title="CIF" value={formatRial(calc.cif)} />
              <ResultCard title="گمرکی" value={formatRial(calc.gomraki)} />
              <ResultCard
                title="مبنای مالیات"
                value={formatRial(calc.mabnayeMaliat)}
              />
              <ResultCard title="۱۰٪" value={formatRial(calc.dahDarsad)} />
              <ResultCard
                title="هلال احمر"
                value={formatRial(calc.helalAhmar)}
              />
              <ResultCard title="پسماند" value={formatRial(calc.pasmand)} />
              <ResultCard title="واریزی" value={formatRial(calc.varizi)} />
              <ResultCard
                title="(۲٪ مالیات)"
                value={formatRial(calc.maliat2Darsad)}
              />
              <ResultCard title="STD" value={formatRial(calc.STD)} />
              <ResultCard title="VOC" value={formatRial(calc.VOC)} />
              <ResultCard
                title="دوازده هزارم"
                value={formatRial(calc.davazdahHezarom)}
              />
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-between">
              <div className="text-sm font-medium text-slate-700">جمع کل</div>
              <div className="text-lg sm:text-xl font-semibold text-slate-900">
                {formatRial(calc.jameKol)}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 text-xs text-slate-500">
          اگر «گمرکی» را انتخاب کنید، نرخ ارز ثابت و تک‌مقداری است (بدون
          buy/sell).
        </div>
      </div>
    </div>
  );
}

function ResultCard({ title, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-4 bg-white">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="mt-2 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default TariffCalculator;
